import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ButtonSquare, ButtonText } from "../../common/components/Button";
import StatsOverview from "../../features/stats/components/StatsOverview/StatsOverview";
import ReleaseBell from "../../features/releases/components/ReleaseBell";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import type { IProjectGroupWithProjects } from "../../features/projectGroups/state/projectGroupsSlice";
import type IProjectGroupsCompletionItem from "../../features/enduserStats/dataTypes/IProjectGroupsCompletionItem";
import {
  deleteProjectGroup,
  fetchAllCurrentElements,
  getProjectGroupPermissionsFromServer,
  setCurrentProjectGroupPermissions,
  setSelectedProjectGroup,
  setSelectedProjectGroupFromId,
  getAllProjectGroupsWithNestedElements,
  fetchSearchIndex,
  fetchProjectGroupsWithProjects,
  getSearchState,
  // fetchProjectGroupsWithProjects,
} from "../../features/projectGroups/state/projectGroupsSlice";
import ProjectGroupList, {
  PROJECT_GROUP_LIST_COMPONENT_ID,
  PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
} from "../../features/projectGroups/components/ProjectGroupList";
import { getCurrentUser } from "../../features/profile/state/profileSlice";
import handleThreeDotsButtonClickForProjectsAndProjectGroups from "../../common/util/handleThreeDotsButtonClickForProjectsAndProjectGroups";
import {
  closeConfirmationModal,
  openConfirmationModal,
} from "../../common/state/slice/modal/modalSlice";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import { fetchLegals } from "src/features/auth/state/authSlice";
import { setAlert } from "../../features/alert/state/alertsSlice";
import { LEGALS_SECTION_ID } from "src/common/components/Sidebar/Sidebar";
import { useLoading } from "src/common/components/AppState/IsLoading";
import { LoadingState } from "src/common/state/slice/appStateSlice";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { TOUR_VIEW_ANCHORS } from "src/features/onboarding/engine/tourViewAnchors";

const CreatorDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const user = useAppSelector(getCurrentUser);
  const projectGroupsWithNestedElements = useAppSelector(getAllProjectGroupsWithNestedElements);
  const [clickedProjectGroupId, setClickedProjectGroupId] = useState(0);
  const projectGroupListLoadingState = useLoading(PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID);
  const listRequestInFlight = useAppSelector(getSearchState).inFlightListUrl !== null;
  const hasCourseCards = projectGroupsWithNestedElements.length > 0;
  const hasModuleCards = projectGroupsWithNestedElements.some((pg: IProjectGroupWithProjects) =>
    (pg.project_group_elements ?? []).some((pge) => pge.project && pge.project.is_deleted !== true),
  );
  useTourView(
    "dashboard",
    projectGroupListLoadingState !== LoadingState.Loading && !listRequestInFlight,
    TOUR_VIEW_ANCHORS.dashboard.filter((anchor) => {
      if (anchor === "course-card-open") {
        return hasCourseCards;
      }
      if (anchor === "module-card-open") {
        return hasModuleCards;
      }
      return true;
    }),
  );

  const handleClick = (projectGroup: IProjectGroupWithProjects | IProjectGroupsCompletionItem) => {
    const projectGroupId = "id" in projectGroup ? projectGroup.id : projectGroup.projectGroupId;
    void dispatch(
      fetchAllCurrentElements({
        projectGroupId,
        componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
      }),
    );

    void dispatch(getProjectGroupPermissionsFromServer({ projectGroupId })).then((res) => {
      if (!getProjectGroupPermissionsFromServer.fulfilled.match(res)) {
        return;
      }
      dispatch(setCurrentProjectGroupPermissions(res.payload.data));
    });
    navigate(`../courses/${projectGroupId}/list`);

    const pgObject = projectGroupsWithNestedElements.find(
      (pg: IProjectGroupWithProjects) => Number(pg.id) === projectGroupId,
    );
    if (pgObject) {
      dispatch(setSelectedProjectGroup(pgObject));
    }
  };

  const setEditedProjectGroupId = (id: number, projectGroup: IProjectGroupWithProjects) => {
    const pg = projectGroup;
    setClickedProjectGroupId(id);
    void dispatch(
      fetchAllCurrentElements({
        projectGroupId: pg.id,
      }),
    );
    dispatch(setSelectedProjectGroup(pg));
  };

  useEffect(() => {
    const defaultAxiosJwt = axios.defaults.headers.common["Authorization"];
    if (defaultAxiosJwt) {
      void dispatch(fetchLegals({ componentId: LEGALS_SECTION_ID }));
      void dispatch(fetchSearchIndex({}));

      void dispatch(
        fetchProjectGroupsWithProjects({
          componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
          limit: 5,
          start: 0,
        }),
      );
    }
  }, [location.key]);

  return (
    <>
      <header className="bg-white shadow sticky top-0 z-50">
        <div className="mx-auto py-6 px-4 ml-4">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-black">
              {t("pages.dashboard.hello")} {user?.firstname}
            </h1>
            {process.env.REACT_APP_HIDE_RELEASES !== "true" && <ReleaseBell />}
          </div>
        </div>
      </header>
      <div className="w-full h-full max-w-9xl mx-auto pt-2 sm:px-6 lg:px-8 overflow-y-auto personalize-scroll-visible">
        <div className="flex space-x-6 items-end">
          <h2 className="text-2xl text-black font-medium">{t("general.projectGroups")}</h2>
          <ButtonText text={t("general.viewAll")} onClick={() => navigate("courses")} />
        </div>

        <div className="block w-full pt-4">
          <div className="relative inline-flex justify-between items-center w-full">
            <div className="mr-6 h-full" data-tour="dashboard-new-course">
              <ButtonSquare
                innerClassName="grid grid-rows-6 w-32 h-48"
                variant="btn-primary-fill"
                iconColor="stroke-white h-8 w-8"
                onClick={() => navigate("/courses/new")}
                text={t("general.newProjectGroup")}
                textClassName="text-lg"
              />
            </div>
            <ProjectGroupList
              setEditedProjectGroupId={setEditedProjectGroupId}
              projectGroupsWithNestedElements={projectGroupsWithNestedElements}
              handleClick={handleClick}
              handleThreeDotButtonClick={(e: {
                item: "project" | "projectGroup";
                itemId: number;
                action: string;
              }) => {
                setClickedProjectGroupId(e.itemId);
                dispatch(setSelectedProjectGroupFromId(e.itemId));

                void dispatch(
                  fetchAllCurrentElements({
                    projectGroupId: e.itemId,
                    componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
                  }),
                );
                handleThreeDotsButtonClickForProjectsAndProjectGroups({
                  payload: e,
                  deleteCallback: () => {
                    dispatch(openConfirmationModal("deleteProjectGroupConfirmation"));
                  },
                  t,
                  navigate,
                  dispatch: dispatch,
                });
              }}
              simplified
            />
          </div>
        </div>
        <div className="mt-6">
          <StatsOverview />
        </div>

        <ModalConfirmation
          id="deleteProjectGroupConfirmation"
          onClickSubmit={() => {
            dispatch(closeConfirmationModal("deleteProjectGroupConfirmation"));
            dispatch(
              deleteProjectGroup({
                id: clickedProjectGroupId,
                componentId: `project-group-${clickedProjectGroupId}`,
              }),
            )
              .then(() => {
                dispatch(setAlert({ type: "success", msg: t("alerts.projectGroupDeleteSuccess") }));
              })
              .catch(() => {
                dispatch(setAlert({ type: "danger", msg: t("alerts.projectGroupDeleteFail") }));
              });
          }}
          title={t("pages.projectGroup.deletionModal.title")}
          text={t("pages.projectGroup.deletionModal.body")}
          componentId={PROJECT_GROUP_LIST_COMPONENT_ID}
        />
      </div>
    </>
  );
};

export default CreatorDashboard;
